import { z } from 'zod';

import { LoanNoteId, QrpcId } from '../BrandedIds';
import { zodBrandedUuid, zodDateOrString } from '../utils';
import {
  AuthorizedContactPaymentPayer,
  BorrowerPaymentPayer,
  EntityPaymentPayer,
  ThirdPartyPaymentPayer,
} from './Payments';
import { QrpcOccupancyStatus } from './QrpcOccupancyStatus';
import { QrpcReason } from './QrpcReason';

export const QrpcContact = z.discriminatedUnion('type', [
  BorrowerPaymentPayer,
  EntityPaymentPayer,
  AuthorizedContactPaymentPayer,
  ThirdPartyPaymentPayer,
]);
export type QrpcContact = z.infer<typeof QrpcContact>;

export const QrpcResult = z.enum(['noContact', 'contacted', 'leftVoicemail', 'other']);
export type QrpcResult = z.infer<typeof QrpcResult>;

export const QrpcHardshipStatus = z.enum(['temporary', 'permanent']);
export type QrpcHardshipStatus = z.infer<typeof QrpcHardshipStatus>;

export const QrpcWorkoutPlanDiscussed = z.enum(['forbearance', 'repayment', 'flexModification', 'paymentDeferral']);
export type QrpcWorkoutPlanDiscussed = z.infer<typeof QrpcWorkoutPlanDiscussed>;

export const QrpcCallDirection = z.enum(['inbound', 'outbound']);
export type QrpcCallDirection = z.infer<typeof QrpcCallDirection>;

export const Qrpc = z.object({
  id: zodBrandedUuid<QrpcId>(),
  loanNoteId: zodBrandedUuid<LoanNoteId>().optional(),

  date: zodDateOrString,
  contact: QrpcContact,

  qrpcObtainedFlag: z.boolean(),
  defaultReason: QrpcReason.optional(),
  hardshipStatus: QrpcHardshipStatus.optional(),
  occupancyStatus: QrpcOccupancyStatus.optional(),
  abilityToPayFlag: z.boolean().optional(),
  workoutPlansDiscussed: z.array(QrpcWorkoutPlanDiscussed),
  commitmentToResolveDelinquencyFlag: z.boolean().optional(),
  promiseToPayFlag: z.boolean().optional(),
  promiseToPayExpirationDate: zodDateOrString.optional(),

  createdAt: zodDateOrString,
  updatedAt: zodDateOrString,
});
export type Qrpc = z.infer<typeof Qrpc>;

export const QrpcInput = Qrpc.omit({
  id: true,
  loanNoteId: true,
  date: true,
  contact: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  id: zodBrandedUuid<QrpcId>().optional(),
});
export type QrpcInput = z.infer<typeof QrpcInput>;
